'use client'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from '@onclicks/sharedmobilei18n/routing'
import { cn } from '@onclicks/sharedcomponents/ui'
import { Conversation, RoleConversation } from '../../../types/models/conversation'
import { useConversations } from '../../../providers/conversations-provider'
import { useUser } from '../../../providers/user-provider'
import { useMessageriePath } from '../../../hooks/use-messagerie-path'
import { coursesPath } from '../../../paths/paths'
import {
  apercuDernierMessage,
  ConversationLabels,
  horodatageCourt,
  titreConversation,
} from '../../../utils/conversation-labels'
import {
  detailNotificationCourse,
  titreNotificationCourse,
} from '../../../utils/notification-course-labels'
import { CourseStatut } from '../../../types/records/course-statut-record'
import Icon from '../../shared/ui/icon'
import { useTranslations } from 'next-intl'

/**
 * Cloche de l'entête : ce qui attend un gestionnaire, tous canaux confondus —
 * les discussions animées et le suivi des courses.
 *
 * Les deux listes restent séparées plutôt que fondues en une : elles n'ont ni
 * le même cycle de vie ni la même notion de « lu ». Un fil se lit message par
 * message et le serveur en tient le compte ; une notification de course est un
 * évènement passé, que la cloche marque vu d'un bloc à son ouverture.
 *
 * Elle ne montre qu'une poignée de chaque : c'est un rappel, pas un journal.
 */

/** Fils listés dans le menu. Au-delà, on renvoie vers la messagerie. */
const APERCU_MAX = 6

/** Notifications de course listées. Le fil complet reste en base. */
const NOTIFICATIONS_MAX = 5

export const NotificationsBell = ({
  className,
  iconClassName = 'text-white text-xl',
}: {
  className?: string
  /** L'entête est sombre en desktop, clair en mobile : la cloche s'y adapte. */
  iconClassName?: string
}) => {
  const router = useRouter()
  const { me } = useUser()
  const {
    conversations,
    totalNonLus,
    etatTempsReel,
    notifications,
    nombreNonVues,
    marquerNotificationsVues,
  } = useConversations()
  const { cheminMessagerie, estBackOffice } = useMessageriePath()
  const [ouvert, setOuvert] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const t = useTranslations('conversation')
  const tStatut = useTranslations('course_statut')

  const conversationLabels: ConversationLabels = {
    roles: {
      [RoleConversation.Passager]: t('role_passenger'),
      [RoleConversation.Demandeur]: t('role_requester'),
      [RoleConversation.Chauffeur]: t('role_driver'),
      [RoleConversation.Gestionnaire]: t('role_manager'),
    },
    roleFallback: t('role_fallback'),
    deletedAccount: t('deleted_account'),
    dispatch: t('dispatch'),
    courseTitle: (id) => t('course_title', { id: id ?? 0 }),
    courseTitleClient: (id) => t('course_title_client', { id: id ?? 0 }),
    courseTitleDriver: (id) => t('course_title_driver', { id: id ?? 0 }),
    courseTitleWithDispatch: (id) => t('course_title_with_dispatch', { id: id ?? 0 }),
    supportWith: (nom) => t('support_with', { nom }),
    supportDefault: t('support_title'),
    noMessages: t('no_message'),
    youPrefix: t('you_prefix'),
    justNow: t('just_now'),
    today: t('today'),
    yesterday: t('yesterday'),
  }

  const notificationLabels = {
    newRide: (id: number) => t('notification_new_ride', { id }),
    rideModified: (id: number) => t('notification_ride_modified', { id }),
    rideAssigned: (id: number) => t('notification_ride_assigned', { id }),
    rideStatus: (id: number, statut: string) =>
      t('notification_ride_status', { id, status: statut }),
    priceLine: (from: string, to: string) =>
      t('notification_price_line', { from, to }),
    driverFallback: t('notification_driver_fallback'),
    statusChanged: tStatut('status_changed'),
    statutLabels: {
      [CourseStatut.PaymentAttente]: tStatut('payment_pending'),
      [CourseStatut.Dispatching]: tStatut('dispatching'),
      [CourseStatut.PreReserve]: tStatut('pre_reserved'),
      [CourseStatut.Affecte]: tStatut('assigned'),
      [CourseStatut.Annulee]: tStatut('cancelled'),
      [CourseStatut.ClientAbsent]: tStatut('client_absent'),
      [CourseStatut.FausseCourse]: tStatut('fake_ride'),
      [CourseStatut.Terminee]: tStatut('completed'),
      [CourseStatut.EnCours]: tStatut('in_progress'),
      [CourseStatut.ChauffeurEnRoute]: tStatut('driver_en_route'),
      [CourseStatut.ChauffeurSurPlace]: tStatut('driver_on_site'),
    },
  }

  useEffect(() => {
    if (!ouvert) return

    const surClicExterieur = (evenement: MouseEvent) => {
      if (!menuRef.current?.contains(evenement.target as Node)) setOuvert(false)
    }

    document.addEventListener('mousedown', surClicExterieur)

    return () => document.removeEventListener('mousedown', surClicExterieur)
  }, [ouvert])

  // Un visiteur non connecté n'a pas de messagerie.
  if (!me) return null

  // Les fils non lus passent devant : la cloche sert à traiter ce qui attend,
  // pas à parcourir l'historique.
  const apercu = [...conversations]
    .sort((a, b) => Number(b.nombreNonLus > 0) - Number(a.nombreNonLus > 0))
    .slice(0, APERCU_MAX)

  const apercuNotifications = notifications.slice(0, NOTIFICATIONS_MAX)
  const total = totalNonLus + nombreNonVues

  const allerVers = (conversation?: Conversation) => {
    setOuvert(false)
    router.push(conversation ? `${cheminMessagerie}?fil=${conversation.id}` : cheminMessagerie)
  }

  const allerVersCourse = (idCourse: number) => {
    setOuvert(false)
    router.push(`${coursesPath}/${idCourse}/view`)
  }

  /**
   * Ouvrir la cloche vaut prise de connaissance du suivi des courses : elles
   * sont montrées d'un bloc, pas ouvertes une par une comme un fil.
   */
  const basculer = () => {
    const ouverture = !ouvert
    setOuvert(ouverture)

    if (ouverture) void marquerNotificationsVues()
  }

  return (
    <div className={cn('relative', className)} ref={menuRef}>
      <button
        type="button"
        onClick={basculer}
        aria-label={total > 0 ? t('notifications_pending_aria', { count: total }) : t('notifications_aria')}
        className="relative -m-2 inline-flex items-center justify-center rounded-md p-2"
      >
        {/* La couleur passe avant la pastille : elle se voit du coin de l'œil,
            là où un chiffre de 10 pixels demande de regarder. */}
        <Icon
          name="notifications"
          className={cn(iconClassName, total > 0 && 'text-primary animate-pulse')}
        />

        {total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-black flex items-center justify-center tabular-nums">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {ouvert && (
        <div className="absolute right-0 mt-3 w-80 max-w-[90vw] bg-black border border-white/10 rounded-md shadow-xl z-[60] overflow-hidden">
          {apercuNotifications.length > 0 && (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <p className="text-sm font-bold text-white">{t('rides_section_title')}</p>
                {nombreNonVues > 0 && (
                  <span className="text-[10px] font-black text-primary tabular-nums">
                    {t('new_count', { count: nombreNonVues })}
                  </span>
                )}
              </div>

              <ul className="divide-y divide-white/5">
                {apercuNotifications.map((notification) => (
                  <li key={notification.id}>
                    <button
                      type="button"
                      onClick={() => allerVersCourse(notification.idCourse)}
                      className="w-full text-left px-4 py-3 hover:bg-white/10 transition-all"
                    >
                      <div className="flex items-baseline gap-2">
                        <p className="flex-1 truncate text-sm font-bold text-white">
                          {titreNotificationCourse(notification, notificationLabels)}
                        </p>
                        <span className="shrink-0 text-[10px] text-white/40 tabular-nums">
                          {horodatageCourt(notification.horodatage, conversationLabels)}
                        </span>
                      </div>
                      <p className="truncate text-xs text-white/50">
                        {detailNotificationCourse(notification, notificationLabels)}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <p className="text-sm font-bold text-white">{t('title')}</p>
            {etatTempsReel !== 'connecte' && (
              <span className="text-[10px] text-amber-400">
                {etatTempsReel === 'connexion' ? t('reconnecting') : t('offline')}
              </span>
            )}
          </div>

          {apercu.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-white/50">
              {t('no_discussions_yet')}
            </p>
          ) : (
            <ul className="max-h-96 overflow-y-auto divide-y divide-white/5">
              {apercu.map((conversation) => (
                <li key={conversation.id}>
                  <button
                    type="button"
                    onClick={() => allerVers(conversation)}
                    className="w-full text-left px-4 py-3 hover:bg-white/10 transition-all"
                  >
                    <div className="flex items-baseline gap-2">
                      <p
                        className={cn(
                          'flex-1 truncate text-sm text-white',
                          conversation.nombreNonLus > 0 ? 'font-black' : 'font-normal',
                        )}
                      >
                        {titreConversation(conversation, conversationLabels, estBackOffice)}
                      </p>
                      <span className="shrink-0 text-[10px] text-white/40 tabular-nums">
                        {horodatageCourt(conversation.dernierMessage?.envoyeLe, conversationLabels)}
                      </span>
                      {conversation.nombreNonLus > 0 && (
                        <span className="shrink-0 min-w-4 h-4 px-1 rounded-full bg-primary text-white text-[10px] font-black flex items-center justify-center tabular-nums">
                          {conversation.nombreNonLus}
                        </span>
                      )}
                    </div>
                    <p className="truncate text-xs text-white/50">
                      {apercuDernierMessage(conversation, me.idContact, conversationLabels)}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={() => allerVers()}
            className="w-full px-4 py-3 text-left text-xs font-bold text-primary border-t border-white/10 hover:bg-white/10 transition-all"
          >
            {t('view_all_discussions')}
          </button>
        </div>
      )}
    </div>
  )
}

export default NotificationsBell
